import {
  Section,
  SectionBody,
  SectionDescription,
  SectionHeader,
  SectionIcon,
  SectionTitle,
  SectionTitleRow,
} from '@campoint/odi-ui';
import { TaskAlt } from '@campoint/odi-ui-icons';
import { Center, Flex } from '@chakra-ui/react';
import type { History } from 'history';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import {
  UrlFragment,
  UrlFragmentScrollToTarget,
} from '../../../../components/Layout/UrlFragmentScrollToTarget';
import { FeatureName, useFeature } from '../../../../flags';
import { RouteReferrerState, routes } from '../../../../routes/routesConfig';
import HomeTaskCardItem, {
  HomeTaskCardItemProps,
} from '../../components/Home/TaskCard/HomeTaskCardItem';
import { EmailVerificationRouterState } from '../../components/Home/VerifyEmailModal/VerifyEmailModal';

export const clickableTaskItemStates: HomeTaskCardItemProps['state'][] = [
  'todo',
  'error',
];

type TaskItemProps = { state: HomeTaskCardItemProps['state'] };

type HistoryState = RouteReferrerState | EmailVerificationRouterState;

export const useHistoryHandler = (
  fn: (history: History<HistoryState>) => void,
  deps: React.DependencyList = []
) => {
  const history = useHistory<HistoryState>();
  return React.useCallback(() => fn(history), [fn, history, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
};

const referrer = routes.home.path;

const TaskItemAccount: React.FC<TaskItemProps> = ({ state }) => (
  <HomeTaskCardItem category="account" state={state} />
);

const TaskItemAge: React.FC<TaskItemProps> = ({ state }) => {
  const documentFlag = useFeature(FeatureName.document);

  const navigateToWizard = useHistoryHandler((history) =>
    history.push(routes.wizardVerification.path, { referrer })
  );

  const navigateToDocumentPage = useHistoryHandler((history) =>
    history.push(routes.documentsActors.path, { referrer })
  );

  const shouldNavigateToDocumentPage = documentFlag && state !== 'todo';

  return (
    <HomeTaskCardItem
      category="age"
      state={state}
      isClickable={clickableTaskItemStates.includes(state)}
      onClick={
        shouldNavigateToDocumentPage ? navigateToDocumentPage : navigateToWizard
      }
    />
  );
};

const TaskItemProfile: React.FC<TaskItemProps> = ({ state }) => {
  const navigateToWizard = useHistoryHandler((history) =>
    history.push(routes.wizardProfile.path, { referrer })
  );

  return (
    <HomeTaskCardItem
      category="profile"
      state={state}
      isClickable={clickableTaskItemStates.includes(state)}
      onClick={navigateToWizard}
    />
  );
};

const TaskItemModelReleaseForm: React.FC<TaskItemProps> = ({ state }) => {
  const navigateToMrfWizard = useHistoryHandler((history) => {
    history.push(routes.wizardMRF.path, { referrer });
  }, []);

  return (
    <HomeTaskCardItem
      category="modelReleaseForm"
      state={state}
      isClickable={clickableTaskItemStates.includes(state)}
      onClick={navigateToMrfWizard}
    />
  );
};

export type TaskSectionProps = {
  isAccountEmailVerified: boolean;
  steps: {
    [key in HomeTaskCardItemProps['category']]: HomeTaskCardItemProps['state'];
  };
};

const TaskSection: React.FC<TaskSectionProps> = (props) => {
  const { t } = useTranslation(['home']);

  return (
    <Section>
      <SectionHeader pb={[3, 0]}>
        <UrlFragmentScrollToTarget id={UrlFragment.Tasks} />
        <SectionTitleRow>
          <SectionIcon as={TaskAlt} />
          <SectionTitle>{t('home:heading.LosGehts')}</SectionTitle>
        </SectionTitleRow>
        <SectionDescription>
          {t('home:cards.tasks.subtitle')}
        </SectionDescription>
      </SectionHeader>
      <Center>
        <SectionBody
          as={Flex}
          gap={4}
          flexDir={{ base: 'column', xl: 'row' }}
          flexWrap={{ base: 'nowrap', xl: 'wrap' }}
          w={'full'}
          maxW={['container.lg', 'full']}
        >
          <TaskItemAccount state={props.steps.account} />
          <TaskItemAge state={props.steps.age} />
          <TaskItemProfile state={props.steps.profile} />
          <TaskItemModelReleaseForm state={props.steps.modelReleaseForm} />
        </SectionBody>
      </Center>
    </Section>
  );
};

export default TaskSection;
